/**
 * WP12b — Kuchnia Kości (MgCooking): memory 6 par potraw.
 * Nagrody wyłącznie przez core/RunClient. Brak karzącego game-over.
 */
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { runClient } from '../../core/RunClient.js';
import {
  type CookingState,
  type DishDef,
  DISHES,
  PEEK_MS,
  ROUND_MS,
  canFlip,
  createCookingState,
  dishById,
  flipCard,
  resolveMismatch,
  buildRunStats,
} from './cookingLogic.js';

export type MgCookingData = {
  entryPoint?: 'phone' | 'console' | 'world';
  gameId?: string;
};

type CardView = {
  root: Phaser.GameObjects.Container;
  back: Phaser.GameObjects.Rectangle;
  face: Phaser.GameObjects.Rectangle;
  emoji: Phaser.GameObjects.Text;
  label: Phaser.GameObjects.Text;
};

const COLS = 4;
const ROWS = 3;
const CARD_W = Math.max(MIN_TOUCH + 40, 120);
const CARD_H = Math.max(MIN_TOUCH + 48, 130);
const GAP = 16;

export class MgCookingScene extends BaseScene {
  static readonly KEY = 'MgCooking';

  private state!: CookingState;
  private cardViews: CardView[] = [];
  private runId: string | null = null;
  private startedAt = 0;
  private ending = false;
  private entryPoint: MgCookingData['entryPoint'];
  private starsText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private focusIndex = 0;
  private peekTimer: Phaser.Time.TimerEvent | null = null;
  private roundTimer: Phaser.Time.TimerEvent | null = null;

  constructor() {
    super(MgCookingScene.KEY);
  }

  init(data: MgCookingData = {}): void {
    this.entryPoint = data.entryPoint;
    this.ending = false;
    this.runId = null;
    this.cardViews = [];
    this.peekTimer = null;
    this.roundTimer = null;
  }

  create(data: MgCookingData = {}): void {
    this.entryPoint = data.entryPoint ?? this.entryPoint;
    this.state = createCookingState();
    this.startedAt = this.time.now;
    this.cameras.main.setBackgroundColor(0xffe4c4);

    this.drawChrome();
    this.drawBoard();
    this.syncBoard();
    this.bindKeyboard();

    void this.beginRun();

    this.roundTimer = this.time.addEvent({
      delay: ROUND_MS,
      callback: () => {
        if (!this.ending) void this.endRound('timeout');
      },
    });

    this.events.once('shutdown', () => this.cleanup());
  }

  update(): void {
    if (this.ending || !this.timerText) return;
    const left = Math.max(0, ROUND_MS - (this.time.now - this.startedAt));
    const sec = Math.ceil(left / 1000);
    this.timerText.setText(`⏱ ${sec} s`);
  }

  private cleanup(): void {
    this.peekTimer?.remove(false);
    this.roundTimer?.remove(false);
    this.input.keyboard?.off('keydown');
  }

  private resolveReturnTo(): string {
    if (this.entryPoint === 'console') return 'ConsoleHub';
    if (this.entryPoint === 'phone') return 'Town';
    return 'Cafe';
  }

  private async beginRun(): Promise<void> {
    try {
      const { runId } = await runClient.startRun('cooking');
      this.runId = runId;
    } catch {
      // offline / błąd — gra lokalnie, bez zł (PLAN §4)
      this.runId = null;
    }
  }

  private drawChrome(): void {
    const cx = this.gameW / 2;

    this.add
      .text(cx, 36, 'Kuchnia Kości', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '36px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(cx, 76, 'Znajdź pary potraw — bez pośpiechu!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5);

    this.starsText = this.add
      .text(cx - 120, 112, '⭐ 0', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.timerText = this.add
      .text(cx + 120, 112, `⏱ ${Math.ceil(ROUND_MS / 1000)} s`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.hintText = this.add
      .text(cx, this.gameH - 36, 'Dotknij kartę · strzałki + Enter · Wyjdź w każdej chwili', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5);

    UiKit.button(this, {
      x: 110,
      y: 48,
      label: '← Wyjdź',
      width: 160,
      height: MIN_TOUCH,
      fill: 0xb8e6d0,
      onClick: () => {
        void this.endRound('exit');
      },
    });
  }

  private drawBoard(): void {
    const boardW = COLS * CARD_W + (COLS - 1) * GAP;
    const boardH = ROWS * CARD_H + (ROWS - 1) * GAP;
    const originX = (this.gameW - boardW) / 2 + CARD_W / 2;
    const originY = 170 + CARD_H / 2;

    this.cardViews = [];
    for (let i = 0; i < this.state.cards.length; i++) {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const x = originX + col * (CARD_W + GAP);
      const y = originY + row * (CARD_H + GAP);

      const back = this.add
        .rectangle(0, 0, CARD_W, CARD_H, 0xffb6c1)
        .setStrokeStyle(4, 0xffffff);
      const face = this.add
        .rectangle(0, 0, CARD_W, CARD_H, 0xfff8f0)
        .setStrokeStyle(4, 0xffffff)
        .setVisible(false);
      const emoji = this.add
        .text(0, -12, '?', {
          fontSize: '42px',
        })
        .setOrigin(0.5)
        .setVisible(false);
      const label = this.add
        .text(0, 36, '', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '16px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setVisible(false);

      // rewers — wzór kości
      const backIcon = this.add
        .text(0, 0, '🦴', { fontSize: '36px' })
        .setOrigin(0.5);

      const root = this.add.container(x, y, [back, face, backIcon, emoji, label]);
      root.setSize(CARD_W, CARD_H);
      root.setInteractive(
        new Phaser.Geom.Rectangle(-CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H),
        Phaser.Geom.Rectangle.Contains,
      );
      root.setData('cardIndex', i);
      root.setData('backIcon', backIcon);

      root.on('pointerdown', () => this.onCardTap(i));

      this.cardViews.push({ root, back, face, emoji, label });
    }
  }

  private bindKeyboard(): void {
    const kb = this.input.keyboard;
    if (!kb) return;
    kb.on('keydown', (ev: KeyboardEvent) => {
      if (this.ending) return;
      const n = this.state.cards.length;
      if (ev.key === 'ArrowRight') {
        this.focusIndex = (this.focusIndex + 1) % n;
        this.highlightFocus();
      } else if (ev.key === 'ArrowLeft') {
        this.focusIndex = (this.focusIndex - 1 + n) % n;
        this.highlightFocus();
      } else if (ev.key === 'ArrowDown') {
        this.focusIndex = (this.focusIndex + COLS) % n;
        this.highlightFocus();
      } else if (ev.key === 'ArrowUp') {
        this.focusIndex = (this.focusIndex - COLS + n) % n;
        this.highlightFocus();
      } else if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        this.onCardTap(this.focusIndex);
      } else if (ev.key === 'Escape') {
        void this.endRound('exit');
      }
    });
  }

  private highlightFocus(): void {
    for (let i = 0; i < this.cardViews.length; i++) {
      const v = this.cardViews[i]!;
      const focused = i === this.focusIndex;
      v.back.setStrokeStyle(focused ? 5 : 4, focused ? 0xffd700 : 0xffffff);
      v.face.setStrokeStyle(focused ? 5 : 4, focused ? 0xffd700 : 0xffffff);
    }
  }

  private onCardTap(index: number): void {
    if (this.ending) return;
    if (!canFlip(this.state, index)) return;

    this.focusIndex = index;
    const result = flipCard(this.state, index);
    this.state = result.state;
    this.syncBoard();
    this.starsText.setText(`⭐ ${this.state.stars}`);

    if (result.matched) {
      this.pulseMatch(result.matched);
      if (result.justCompleted) {
        this.showCakeFinale();
      }
      return;
    }

    if (result.mismatch) {
      this.peekTimer?.remove(false);
      this.peekTimer = this.time.delayedCall(PEEK_MS, () => {
        this.state = resolveMismatch(this.state);
        this.syncBoard();
      });
    }
  }

  private pulseMatch(pair: [number, number]): void {
    for (const i of pair) {
      const v = this.cardViews[i];
      if (!v) continue;
      this.tweens.add({
        targets: v.root,
        scaleX: 1.08,
        scaleY: 1.08,
        duration: 120,
        yoyo: true,
      });
    }
  }

  private syncBoard(): void {
    for (let i = 0; i < this.state.cards.length; i++) {
      const card = this.state.cards[i]!;
      const view = this.cardViews[i]!;
      const showFace = card.faceUp || card.matched;
      const dish: DishDef = dishById(card.dishId);

      view.back.setVisible(!showFace);
      const backIcon = view.root.getData('backIcon') as Phaser.GameObjects.Text | undefined;
      backIcon?.setVisible(!showFace);

      view.face.setVisible(showFace);
      view.face.setFillStyle(card.matched ? dish.color : 0xfff8f0);
      view.emoji.setVisible(showFace);
      view.emoji.setText(dish.emoji);
      view.label.setVisible(showFace);
      view.label.setText(dish.namePl);

      // asset key zarezerwowany pod WP13 (tekstura gdy preload)
      view.root.setData('assetKey', dish.assetKey);

      if (card.matched) {
        view.root.disableInteractive();
      } else if (!this.state.locked && !this.state.complete) {
        view.root.setInteractive(
          new Phaser.Geom.Rectangle(-CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H),
          Phaser.Geom.Rectangle.Contains,
        );
      }
    }
    this.highlightFocus();
  }

  private showCakeFinale(): void {
    this.hintText.setText('Pyszny tort dla wszystkich! 🎂');
    const cx = this.gameW / 2;
    const cy = this.gameH / 2;

    const veil = this.add.rectangle(cx, cy, this.gameW, this.gameH, 0x4a3f55, 0.45).setDepth(100);
    const cake = this.add
      .text(cx, cy - 20, '🎂', { fontSize: '120px' })
      .setOrigin(0.5)
      .setDepth(101)
      .setScale(0.2);
    const msg = this.add
      .text(cx, cy + 80, 'Finał — tort z kościarnianej kuchni!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(101);

    this.tweens.add({
      targets: cake,
      scaleX: 1,
      scaleY: 1,
      duration: 500,
      ease: 'Back.easeOut',
    });

    // konfetti-emoji
    for (let i = 0; i < 12; i++) {
      const e = this.add
        .text(cx + (i - 6) * 40, cy - 120, DISHES[i % DISHES.length]!.emoji, {
          fontSize: '28px',
        })
        .setDepth(101)
        .setAlpha(0);
      this.tweens.add({
        targets: e,
        y: cy + 140,
        alpha: 1,
        duration: 800 + i * 40,
        ease: 'Sine.easeOut',
      });
    }

    this.time.delayedCall(1800, () => {
      veil.destroy();
      cake.destroy();
      msg.destroy();
      void this.endRound('complete');
    });
  }

  private async endRound(reason: 'complete' | 'timeout' | 'exit'): Promise<void> {
    if (this.ending) return;
    this.ending = true;
    this.peekTimer?.remove(false);
    this.roundTimer?.remove(false);

    const durationMs = Math.max(0, Math.floor(this.time.now - this.startedAt));
    const stats = buildRunStats(this.state, durationMs);
    let rewardZl = 0;

    if (this.runId) {
      try {
        const result = await runClient.finishRun(this.runId, stats);
        rewardZl = result.rewardZl;
      } catch {
        // za krótko / offline — bez zł, bez kary (stub lub serwer)
        rewardZl = 0;
      }
    }

    // timeout z postępem: miły komunikat, nie fail
    if (reason === 'timeout' && !this.state.complete) {
      this.hintText.setText('Koniec czasu — super próba! ⭐');
    }

    getSceneRouter().go('Reward', {
      rewardZl,
      returnTo: this.resolveReturnTo(),
      stars: stats.stars,
      gameId: 'cooking',
    });
  }
}

export default MgCookingScene;
